<script setup>
import { ref, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import * as authApi from '@/api/auth'
import { bindInvite } from '@/api/invite'
import * as validation from '@/utils/validation'
import { clearStoredAttribution, getAttributionPayload } from '@/utils/attribution'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const loginMethod = ref(route.query.method === 'password' ? 'password' : 'code')
const phone = ref('')
const code = ref('')
const password = ref('')

// UI state
const loading = ref(false)
const error = ref('')
const codeButtonLoading = ref(false)

// Countdown logic
const countdown = ref(0)
let timer = null

async function bindStoredInviteAfterLogin() {
  const attribution = getAttributionPayload()
  if (!attribution.invite_code) {
    clearStoredAttribution()
    return
  }

  try {
    await bindInvite(attribution)
    clearStoredAttribution()
  } catch (err) {
    console.warn('Bind invite failed after login:', err)
  }
}

const sendCode = async () => {
  error.value = ''

  if (!validation.validatePhone(phone.value)) {
    error.value = validation.getPhoneErrorMessage()
    return
  }

  codeButtonLoading.value = true
  try {
    const response = await authApi.sendVerificationCode(
      phone.value,
      loginMethod.value === 'code' ? 2 : 1 // 2=login, 1=register (for verification)
    )

    if (response.code === 1) {
      // Success - start countdown
      countdown.value = 60
      timer = setInterval(() => {
        countdown.value--
        if (countdown.value <= 0) {
          clearInterval(timer)
        }
      }, 1000)
    } else {
      error.value = response.message || '验证码发送失败'
    }
  } catch (err) {
    error.value = '验证码发送失败，请检查网络连接'
    console.error('Send code error:', err)
  } finally {
    codeButtonLoading.value = false
  }
}

const handleLogin = async () => {
  error.value = ''

  // 登录时不需要勾选服务条款，只需要验证登录信息
  if (loginMethod.value === 'code') {
    const validation_result = validation.validateCodeLogin(phone.value, code.value)
    if (!validation_result.isValid) {
      error.value = validation_result.errors[0]
      return
    }
  } else {
    const validation_result = validation.validateLoginCredentials(phone.value, password.value)
    if (!validation_result.isValid) {
      error.value = validation_result.errors[0]
      return
    }
  }

  loading.value = true
  try {
    let response

    if (loginMethod.value === 'code') {
      response = await authApi.loginWithCode(phone.value, code.value, getAttributionPayload())
    } else {
      response = await authApi.loginWithPassword(phone.value, password.value)
    }

    if (response.code === 1) {
      // Login successful - save token and user info to store
      authStore.setToken(response.data.token)
      authStore.setUserInfo({
        user_id: response.data.user_id,
        phone: response.data.phone,
        nickname: response.data.nickname,
        company: response.data.company,
        user_type: response.data.user_type,
        login_method: loginMethod.value,
        has_password: response.data.has_password ?? true  // 存储是否已设置密码
      })
      await bindStoredInviteAfterLogin()

      // 验证码登录且未设置过密码，引导去设置密码
      if (loginMethod.value === 'code' && response.data.has_password === false) {
        router.push('/account-settings')
      } else {
        const redirect = router.currentRoute.value.query.redirect
        router.push(redirect || '/')
      }
    } else {
      error.value = response.message || '登录失败'
    }
  } catch (err) {
    error.value = '登录失败，请检查网络连接'
    console.error('Login error:', err)
  } finally {
    loading.value = false
  }
}

onUnmounted(() => {
  if (timer) clearInterval(timer)
})
</script>

<template>
  <div class="auth-container">
    <div class="auth-card">
      <h2 class="auth-title">登录水芯</h2>

      <!-- Error Message -->
      <div v-if="error" class="error-message">{{ error }}</div>

      <!-- Tabs -->
      <div class="auth-tabs">
        <div
          class="tab-item"
          :class="{ active: loginMethod === 'code' }"
          @click="loginMethod = 'code'"
        >
          验证码登录
        </div>
        <div
          class="tab-item"
          :class="{ active: loginMethod === 'password' }"
          @click="loginMethod = 'password'"
        >
          密码登录
        </div>
      </div>

      <form @submit.prevent="handleLogin" class="auth-form">
        <!-- Phone Number (Common) -->
        <div class="form-group">
          <div class="input-wrapper">
             <span class="prefix">+86</span>
             <input
              type="tel"
              id="phone"
              v-model="phone"
              required
              placeholder="请输入手机号码"
              maxlength="11"
              :disabled="loading || codeButtonLoading"
            />
          </div>
        </div>

        <!-- SMS Code Input -->
        <div class="form-group" v-if="loginMethod === 'code'">
          <div class="code-wrapper">
            <input
              type="text"
              v-model="code"
              required
              placeholder="请输入验证码"
              maxlength="6"
              :disabled="loading"
            />
            <button
              type="button"
              class="btn-code"
              @click="sendCode"
              :disabled="countdown > 0 || codeButtonLoading || loading"
            >
              {{ countdown > 0 ? `${countdown}s` : '获取验证码' }}
            </button>
          </div>
        </div>

        <!-- Password Input -->
        <div class="form-group" v-if="loginMethod === 'password'">
          <div class="input-wrapper">
            <input
              type="password"
              v-model="password"
              required
              placeholder="请输入密码"
              :disabled="loading"
            />
          </div>
        </div>

        <div class="form-options">
          <p class="terms-info">
            <router-link to="/terms">服务条款</router-link> · 
            <router-link to="/privacy">隐私政策</router-link>
          </p>
          <router-link to="/forgot-password" class="forgot-link" v-if="loginMethod === 'password'">忘记密码？</router-link>
        </div>

        <button type="submit" class="btn-submit" :disabled="loading">
          {{ loading ? '正在登录...' : '立即登录' }}
        </button>
      </form>
      <div class="auth-footer">
        <p>还没有账号？ <router-link to="/register">立即注册</router-link></p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.auth-container {
  min-height: calc(100vh - 200px);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f4f6f9;
  padding: 40px 20px;
}

.auth-card {
  background: white;
  padding: 40px 40px 30px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  width: 100%;
  max-width: 400px;
  border-top: 4px solid #005eb8;
}

.auth-title {
  font-size: 24px;
  margin-bottom: 24px;
  color: #1a1a1a;
  text-align: center;
}

.error-message {
  background-color: #fff2f0;
  border: 1px solid #ffccc7;
  color: #d84315;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-size: 14px;
}

.auth-tabs {
  display: flex;
  border-bottom: 1px solid #eee;
  margin-bottom: 24px;
}

.tab-item {
  flex: 1;
  text-align: center;
  padding: 10px;
  font-size: 16px;
  color: #666;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
}

.tab-item:hover {
  color: #005eb8;
}

.tab-item.active {
  color: #005eb8;
  font-weight: 600;
  border-bottom-color: #005eb8;
}

.form-group {
  margin-bottom: 20px;
}

.input-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
}

.prefix {
  background: #f5f5f5;
  padding: 10px 12px;
  font-size: 14px;
  color: #666;
  border-right: 1px solid #ccc;
}

.input-wrapper input {
  border: none;
  flex: 1;
  padding: 10px 12px;
  font-size: 14px;
  outline: none;
}

.input-wrapper:focus-within {
  border-color: #005eb8;
}

.code-wrapper {
  display: flex;
  gap: 10px;
}

.code-wrapper input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
}

.code-wrapper input:focus {
  border-color: #005eb8;
}

.btn-code {
  padding: 0 16px;
  background: #eef4fb;
  color: #005eb8;
  border: 1px solid #cce0f5;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  min-width: 90px;
  white-space: nowrap;
}

.btn-code:hover:not(:disabled) {
  background: #dbe9f6;
}

.btn-code:disabled {
  background: #f5f5f5;
  color: #999;
  border-color: #ddd;
  cursor: not-allowed;
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  margin-bottom: 24px;
}

.terms-info {
  margin: 0;
  color: #999;
}

.terms-info a {
  color: #005eb8;
  text-decoration: none;
  margin: 0 4px;
}

.terms-info a:hover {
  text-decoration: underline;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #555;
  cursor: pointer;
}

.checkbox-label a, .forgot-link {
  color: #005eb8;
  text-decoration: none;
}

.checkbox-label a:hover, .forgot-link:hover {
  text-decoration: underline;
}

.btn-submit {
  width: 100%;
  padding: 12px;
  background-color: #005eb8;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.2s;
}

.btn-submit:hover {
  background-color: #004a94;
}

.auth-footer {
  margin-top: 24px;
  text-align: center;
  font-size: 14px;
  color: #666;
}

.auth-footer a {
  color: #005eb8;
  text-decoration: none;
  font-weight: 600;
}

.auth-footer a:hover {
  text-decoration: underline;
}
</style>
